import { useState, useEffect, useCallback } from 'react';
import { Download, Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useExportHistory } from '@/hooks/useExportHistory';
import { ExportHistoryHeader } from './ExportHistoryHeader';
import { ExportHistoryItem } from './ExportHistoryItem';
import { useDebounce } from '@/hooks/useDebounce';

interface Props {
  /** 面板是否可见 —— 每次从隐藏变可见时重新拉一次，不做轮询。 */
  open: boolean;
}

/**
 * My Downloads 面板本体（搜索 + 列表 + 加载更多）。
 *
 * R-044 把导出历史从顶栏独立图标收进了 ⋯ 菜单，弹层由 TopBarMoreMenu 用
 * PopoverAnchor 挂在 ⋯ 按钮上，所以这里只负责内容，不再自带 trigger。
 */
export function ExportHistoryPanel({ open }: Props) {
  const { t } = useTranslation('chrome');
  const [searchTerm, setSearchTerm] = useState('');

  // 使用防抖处理搜索
  const debouncedSearch = useDebounce(searchTerm, 300);

  // 使用 Hook 获取数据
  const { data, loading, error, hasMore, loadMore, refetch } = useExportHistory({
    pageSize: 5,
    search: debouncedSearch,
  });

  // 每次打开面板时都刷新数据，确保获取最新数据
  useEffect(() => {
    if (open) {
      void refetch();
    }
  }, [open, refetch]);

  // 处理加载更多
  const handleLoadMore = useCallback(async () => {
    if (!loading && hasMore) {
      await loadMore();
    }
  }, [loading, hasMore, loadMore]);

  return (
    <div className="flex max-h-[600px] flex-col">
      <ExportHistoryHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {loading && data.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          <p>{t('exportHistory.loading')}</p>
        </div>
      ) : error ? (
        <div className="px-4 py-8 text-center">
          <div className="flex flex-col items-center gap-3">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                {t('exportHistory.errorTitle')}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="mt-2"
              >
                {t('common:action.retry', 'Retry')}
              </Button>
            </div>
          </div>
        </div>
      ) : data.length === 0 ? (
        <div className="px-4 py-12 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-full bg-muted p-3">
              <Download className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                {t('exportHistory.emptyTitle')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('exportHistory.emptyHint')}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          <ScrollArea className="h-[300px]">
            <div className="py-2">
              {data.map((item) => (
                <ExportHistoryItem key={item.id} item={item} />
              ))}
            </div>
          </ScrollArea>
          {hasMore && (
            <div className="px-4 py-3 border-t shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('exportHistory.loadMoreLoading')}
                  </>
                ) : (
                  t('exportHistory.loadMore')
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
